import { Grid, MenuItem } from '@material-ui/core';
import { useFormikContext } from 'formik';
import * as React from 'react';
import { useObservable } from 'react-use-observable';
import { CurrencyId } from 'v2/interfaces/financial';
import eduzzAccountsService from 'v2/services/eduzzAccount';
import financialService from 'v2/services/financial';

import { accountNatureTypes, accountTypes, BANCO_DO_BRASIL_ID, CAIXA_ECONOMICA_ID, currencyTypes } from '../form';
import { Input } from '../Input';
import { Layout } from '../Layout';
import { useStyles } from './styles';

import type { NewBankAccount } from '../form';

function BankData() {
  const [user] = useObservable(() => eduzzAccountsService.getUser(), []);
  const classes = useStyles();
  const { values } = useFormikContext<NewBankAccount>();
  const isCrossborder = user?.international_account || user?.crossborder;
  const [banks, banksError] = useObservable(() => financialService.listBanks(isCrossborder).pipe(), [isCrossborder]);
  const [operations, operationsError] = useObservable(() => financialService.listOperations().pipe(), []);
  const isLoadingBanks = banks === null && banksError === null;
  const isLoadingOperations = operations === null && operationsError === null;

  if (!user) return null;

  return (
    <Layout title='Dados bancários' description={<strong>Só é possível cadastrar contas que estejam em nome do titular da conta Eduzz</strong>}>
      <Grid container spacing={2} className={classes.content}>
        <Grid item xs={12} sm={8}>
          <Input select name='account_nature' label='Natureza da conta*'>
            {accountNatureTypes.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Input>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Input select name='currency' label='Moeda*'>
            {currencyTypes.map((option) => (
              <MenuItem
                key={option.value}
                value={option.value}
                disabled={(option.value === CurrencyId.USD && !isCrossborder) || (option.value === CurrencyId.BRL && isCrossborder)}
              >
                {option.label}
              </MenuItem>
            ))}
          </Input>
        </Grid>
        <Grid item xs={12}>
          {!isCrossborder && (
            <Input
              name='document_number'
              label='Digite seu CPF ou CNPJ*'
              placeholder='Digite o número'
              fullWidth
              mask='document'
              inputProps={{
                maxLength: 25,
              }}
              inputMode='numeric'
            />
          )}
          {isCrossborder && (
            <Input
              name='document_number'
              label='FEI/EIN Number*'
              placeholder='FEI/EIN'
              inputProps={{
                maxLength: 25,
              }}
              inputMode='numeric'
            />
          )}
        </Grid>
        <Grid item xs={12}>
          <Input
            name='name'
            label='Seu nome completo'
            placeholder='Nome do correntista'
            helperText={<span className={classes.boldHelperText}>Só é possível cadastrar contas que estejam em nome do titular da conta Eduzz</span>}
            disabled
          />
        </Grid>
        <Grid item xs={12}>
          <Input select name='bank_id' label='Escolha seu banco*' disabled={isLoadingBanks}>
            <MenuItem value='not-selected' disabled selected>
              {isLoadingBanks ? 'Carregando...' : 'Selecione um banco'}
            </MenuItem>

            {banks?.map((bank) => (
              <MenuItem key={bank.id} value={bank.id}>
                {bank.value}
              </MenuItem>
            ))}
          </Input>
        </Grid>
        {values.bank_id === CAIXA_ECONOMICA_ID && (
          <Grid item xs={12}>
            <Input select name='operation_id' label='Operações Caixa Econômica*' disabled={isLoadingOperations}>
              <MenuItem value='not-selected' disabled selected>
                {isLoadingOperations ? 'Carregando...' : 'Selecione uma operação'}
              </MenuItem>

              {operations?.map((operation) => (
                <MenuItem key={operation.id} value={operation.id}>
                  {operation.id + ' - ' + operation.operation}
                </MenuItem>
              ))}
            </Input>
          </Grid>
        )}
        <Grid item xs={12}>
          <Input select name='type' label='Tipo de conta*'>
            {accountTypes.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Input>
        </Grid>
        <Grid item xs={12} sm={!isCrossborder ? 8 : 12}>
          <Input
            name='agency'
            label={!isCrossborder ? 'Número da agência*' : 'ABA Routing Number*'}
            mask='numberandzero'
            placeholder='Digite o número de sua agência'
            inputProps={{
              maxLength: 20,
            }}
            inputMode='numeric'
          />
        </Grid>
        {!isCrossborder && (
          <Grid item xs={12} sm={4}>
            <Input
              name='agency_digit'
              label='Dígito (opcional)'
              placeholder='Digite o dígito'
              mask='singledigit'
              inputProps={{
                maxLength: 1,
              }}
              type='number'
            />
          </Grid>
        )}
        <Grid item xs={12} sm={!isCrossborder ? 8 : 12}>
          <Input
            name='account'
            label={!isCrossborder ? 'Número da conta*' : 'Account Number*'}
            placeholder='Digite o número de sua conta'
            mask='numberandzero'
            inputProps={{
              maxLength: 20,
            }}
            inputMode='numeric'
          />
        </Grid>
        {!isCrossborder && (
          <Grid item xs={12} sm={4}>
            <Input
              name='account_digit'
              label='Dígito*'
              placeholder='Digite o dígito'
              mask={values.bank_id === BANCO_DO_BRASIL_ID ? 'bankaccountdigit' : 'numberandzero'}
              inputProps={{
                maxLength: 1,
              }}
              type={values.bank_id === BANCO_DO_BRASIL_ID ? 'text' : 'number'}
            />
          </Grid>
        )}
        {isCrossborder && (
          <Grid item xs={12}>
            <Input
              name='notes'
              label='Observações opcional'
              multiline
              minRows={5}
              fullWidth
              inputProps={{
                maxLength: 255,
              }}
            />
          </Grid>
        )}
      </Grid>
    </Layout>
  );
}

export { BankData };
